// requires typescript upgrade for templates

import { CountryCode, parsePhoneNumberFromString } from "libphonenumber-js"
import { fromPairs, isArray } from "lodash"

// type ArrayWrapper<T extends string> = T | `[${T}]`
export type QueryFieldFormatter = "text" | "phone" | "boolean" | "number"

export interface PhoneOptions {
  defaultCountryCode?: CountryCode
}

export interface NumberOptions {
  type?: "int" | "float"
}

export interface BooleanOptions {
  truthy?: string[]
  falsy?: string[]
}

export interface QueryField {
  key: string
  formatter: QueryFieldFormatter
  options?: PhoneOptions | NumberOptions | BooleanOptions
}


/**
 * If a string is passed normalizes it into a QueryField object.
 * 
 * @param field A QueryField object or a string.
 * @returns A normalized QueryField object.
 */
export const normalizeQueryField = (field: QueryField | string, formatter: QueryFieldFormatter = "text"): QueryField => {
  if (typeof field === "string") {
    return {
      key: field,
      formatter
    }
  }
  return field
}


const parseNumber = (value: string, options: NumberOptions = {}): number => {
  const { type = "int" } = options

  switch (type) {
    case "int":
      return parseInt(value)

    case "float":
      return parseFloat(value)

    default:
      throw Error(`Invalid type "${type}" for number formatter`)
  }

}


const truthy_ = ["true", "t", "yes", "y", "1"]
const falsy_ = ["false", "f", "no", "n", "0"]

const parseBoolean = (value: string, options: BooleanOptions = {}): boolean => {
  const { truthy = truthy_, falsy = falsy_ } = options

  value = value.toLowerCase()

  if (truthy.includes(value)) return true
  if (falsy.includes(value)) return false

  throw Error(`"${value}" is neither truthy or falsy`)
}


const parsePhone = (value: string, options: PhoneOptions = {}): string => {
  const { defaultCountryCode } = options

  const phone = parsePhoneNumberFromString(value, defaultCountryCode)

  if (!phone) {
    throw Error(`Invalid phone number "${value}" for country ${defaultCountryCode}`)
  }

  return phone.format("E.164")
}


export const getQueryFieldVariable = (field: QueryField, query: Record<string, string | (string | null)[] | undefined>) => {
  const { key, formatter, options } = field

  const value = query[key]

  // field does not exist
  if (value === undefined) return

  // do not handle arrays
  if (isArray(value)) return

  switch (formatter) {
    case "text":
      return value

    case "number":
      return parseNumber(value, <NumberOptions>options)

    case "boolean":
      return parseBoolean(value, <BooleanOptions>options)

    case "phone":
      return parsePhone(value, <PhoneOptions>options)

    default:
      throw Error(`Unknown formatter "${formatter}"`)
  }
}

export const getQueryFieldVariables = (fields: QueryField[], query: Record<string, string | (string | null)[] | undefined>): Record<string, any> => {
  return fromPairs(
    fields
      .map(field => {
        let value = undefined
        try {
          value = getQueryFieldVariable(field, query)
        } catch (e) {
          // For debug purposes warn about errors but otherwise ignore them
          console.error(`[Query Error]: Error parsing query param "${field.key}", ${e}`)
        }
        return [`fields.${field.key}`, value]
      })
  )
}